import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Shield, LogOut, User, LayoutDashboard, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../lib/auth';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Testimonials', href: '/testimonials' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Policy', href: '/policy' },
    { name: 'Contact', href: '/contact' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  // Handle navigation click - close mobile menu and scroll to top
  const handleNavClick = () => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  // Handle desktop nav click - scroll to top
  const handleDesktopNavClick = () => {
    window.scrollTo(0, 0);
  };

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Top">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link" onClick={handleDesktopNavClick}>
            <Shield className="h-8 w-8 text-amber-700" strokeWidth={1.5} />
            <div className="flex flex-col">
              <span className="font-heading text-lg font-semibold text-slate-900 leading-tight">
                Tamperis Solutions
              </span>
              <span className="text-xs text-slate-600 hidden sm:block">From Faith to Freedom</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:gap-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase()}`}
                onClick={handleDesktopNavClick}
                className={`text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? 'text-amber-700'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Auth Buttons / User Menu */}
          <div className="hidden md:flex md:items-center md:gap-4">
            <a 
              href="https://calendly.com/tamperis94/30min" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="gap-2 border-amber-600 text-amber-700 hover:bg-amber-50" data-testid="book-call-btn">
                <Calendar className="h-4 w-4" />
                Book a Call
              </Button>
            </a>
            {isAuthenticated() ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="user-menu-trigger">
                    <User className="h-4 w-4" />
                    <span className="max-w-[120px] truncate">{user?.full_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to={user?.role === 'admin' ? '/admin' : '/dashboard'} className="flex items-center gap-2">
                      <LayoutDashboard className="h-4 w-4" />
                      {user?.role === 'admin' ? 'Admin Panel' : 'Dashboard'}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-red-600" data-testid="logout-btn">
                    <LogOut className="h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" onClick={handleDesktopNavClick}>
                  <Button variant="ghost" data-testid="login-btn">Log In</Button>
                </Link>
                <Link to="/register" onClick={handleDesktopNavClick}>
                  <Button className="bg-slate-900 hover:bg-slate-800" data-testid="get-started-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden">
            <button
              type="button"
              className="text-slate-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleNavClick}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    isActive(item.href)
                      ? 'bg-amber-50 text-amber-700'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="border-t border-slate-200 mt-2 pt-4 px-4 flex flex-col gap-2">
                <a 
                  href="https://calendly.com/tamperis94/30min" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button variant="outline" className="w-full gap-2 border-amber-600 text-amber-700">
                    <Calendar className="h-4 w-4" />
                    Book a Call
                  </Button>
                </a>
                {isAuthenticated() ? (
                  <>
                    <Link
                      to={user?.role === 'admin' ? '/admin' : '/dashboard'}
                      onClick={handleNavClick}
                    >
                      <Button className="w-full" variant="outline">
                        {user?.role === 'admin' ? 'Admin Panel' : 'Dashboard'}
                      </Button>
                    </Link>
                    <Button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} variant="ghost" className="w-full text-red-600">
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={handleNavClick}>
                      <Button variant="outline" className="w-full">Log In</Button>
                    </Link>
                    <Link to="/register" onClick={handleNavClick}>
                      <Button className="w-full bg-slate-900 hover:bg-slate-800">Get Started</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
